import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

export interface Notification {
    id: string;
    type: 'connection_request' | 'connection_accepted' | 'connection_declined' | 'connection_removed' | 'announcement_created';
    title: string;
    message: string;
    timestamp: Date;
    read: boolean;
}

interface NotificationsContextValue {
    notifications: Notification[];
    unreadCount: number;
    addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearAll: () => void;
}

const STORAGE_KEY_PREFIX = 'chanco_notifications';

function getStorageKey(userId?: string | number) {
    return userId
        ? `${STORAGE_KEY_PREFIX}_${userId}`
        : STORAGE_KEY_PREFIX;
}

function loadNotifications(
    userId?: string | number,
): Notification[] {
    try {
        const raw = localStorage.getItem(
            getStorageKey(userId),
        );
        if (!raw) return [];
        const parsed = JSON.parse(raw) as Array<
            Notification & { timestamp: string }
        >;
        return parsed.map((n) => ({
            ...n,
            timestamp: new Date(n.timestamp),
        }));
    } catch {
        return [];
    }
}

function saveNotifications(
    notifications: Notification[],
    userId?: string | number,
) {
    localStorage.setItem(
        getStorageKey(userId),
        JSON.stringify(notifications),
    );
}

const NotificationsContext = createContext<NotificationsContextValue>({
    notifications: [],
    unreadCount: 0,
    addNotification: () => {},
    markAsRead: () => {},
    markAllAsRead: () => {},
    clearAll: () => {},
});

interface NotificationsProviderProps {
    children: React.ReactNode;
    userId?: string | number;
}

export function NotificationsProvider({
    children,
    userId,
}: NotificationsProviderProps) {
    const [notifications, setNotifications] = useState<Notification[]>(
        () => loadNotifications(userId),
    );

    // Reload notifications when user changes
    useEffect(() => {
        setNotifications(loadNotifications(userId));
    }, [userId]);

    // Persist to localStorage whenever notifications change
    useEffect(() => {
        saveNotifications(notifications, userId);
    }, [notifications, userId]);

    const unreadCount = useMemo(
        () => notifications.filter((n) => !n.read).length,
        [notifications],
    );

    const addNotification = useCallback(
        (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
            const newNotification: Notification = {
                ...notification,
                id: crypto.randomUUID(),
                timestamp: new Date(),
                read: false,
            };
            setNotifications((prev) => [newNotification, ...prev]);
        },
        [],
    );

    const markAsRead = useCallback((id: string) => {
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }, []);

    const clearAll = useCallback(() => {
        setNotifications([]);
    }, []);

    const value = useMemo(
        () => ({
            notifications,
            unreadCount,
            addNotification,
            markAsRead,
            markAllAsRead,
            clearAll,
        }),
        [notifications, unreadCount, addNotification, markAsRead, markAllAsRead, clearAll],
    );

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationsContext);
}

export default NotificationsContext;
